// Typ zadania: Klasyfikacja trójkąta na podstawie boków a,b,c
// UI: Select „Czy powstaje trójkąt?” (Tak/Nie)
//     Gdy „Tak”: Select „po bokach” i „po kątach”

import SelectInput from "../../../../components/inputs/SelectInput";
import { InlineMath, BlockMath } from "react-katex";

// ───────────────────────── helpers ─────────────────────────
function shuffle(arr){ return arr.sort(()=>Math.random()-0.5); }
function rnd(a,b){ return Math.floor(Math.random()*(b-a+1))+a; }

function isTriangle(a,b,c){
  const [x,y,z] = [a,b,c].sort((p,q)=>p-q);
  return x + y > z;
}

function classifyBySides(a,b,c){
  if (a===b && b===c) return "rownoboczny";
  if (a===b || a===c || b===c) return "rownoramienny";
  return "roznoboczny";
}

function classifyByAngles(a,b,c){
  const [x,y,z] = [a,b,c].sort((p,q)=>p-q);
  const s = x*x + y*y;
  const z2 = z*z;
  if (s === z2) return "prostokatny";
  if (s >  z2) return "ostrokatny";
  return "rozwartokatny";
}

// ───────────────────────── generator ───────────────────────
// opcjonalny parametr: { bucket: "not_triangle" | "iso_eq" | "scalene" }
// kiedy brak parametru – losujemy jak dotąd w pełnym spektrum
export function generateTask(opts = {}) {
  const pick = (arr) => arr[Math.floor(Math.random() * arr.length)];

  let bucket = opts.bucket || null;

  // jeśli nie wymuszono koszyka, losujemy jak wcześniej
  if (!bucket) {
    const all = [
      "equilateral", "isosceles_acute", "isosceles_obtuse",
      "scalene_acute", "right", "scalene_obtuse",
      "not_triangle"
    ];
    bucket = pick(all);
  }

  // jeśli poproszono o koszyk zbiorczy — uszczegóławiamy
  if (bucket === "iso_eq") {
    bucket = pick(["equilateral", "isosceles_acute", "isosceles_obtuse"]);
  } else if (bucket === "scalene") {
    bucket = pick(["scalene_acute", "right", "scalene_obtuse"]);
  }

  let a, b, c;

  switch (bucket) {
    case "equilateral": {
      const k = rnd(3, 10);
      a = b = c = k;
      break;
    }
    case "isosceles_acute": {
      const k = rnd(4, 10);
      a = b = k; c = k - 1;
      break;
    }
    case "isosceles_obtuse": {
      const k = rnd(6, 12);
      a = b = k; c = k + 2;
      break;
    }
    case "right": {
      const triples = [[3,4,5],[5,12,13],[6,8,10],[7,24,25],[8,15,17]];
      [a, b, c] = triples[rnd(0, triples.length - 1)];
      break;
    }
    case "scalene_acute": {
      const sets = [[5,6,7],[6,7,8],[7,8,9],[8,9,10]];
      [a, b, c] = sets[rnd(0, sets.length - 1)];
      break;
    }
    case "scalene_obtuse": {
      const sets = [[6,8,13],[7,10,12],[9,10,18]];
      [a, b, c] = sets[rnd(0, sets.length - 1)];
      break;
    }
    case "not_triangle": {
      // losowo: 50% przypadków x + y = z, 50% przypadków x + y < z
      if (Math.random() < 0.5) {
        // równość
        const sets = [
          [3, 4, 7],
          [5, 5, 10],
          [2, 3, 5],
          [4, 6, 10],
        ];
        [a, b, c] = sets[rnd(0, sets.length - 1)];
      } else {
        // ściśle mniejsza suma: x + y < z
        const x = rnd(2, 9);
        const y = rnd(2, 9);
        const gap = rnd(1, 4);   // „dziura” ponad sumę
        const z = x + y + gap;   // daje x + y < z
        [a, b, c] = [x, y, z];
      }
      break;
    }

    default: {
      // fallback — nie powinno się zdarzyć, ale niech coś wygeneruje
      const sets = [[5,6,7],[3,4,5],[7,10,12],[6,6,6],[3,4,7]];
      [a, b, c] = sets[rnd(0, sets.length - 1)];
    }
  }

  // losowa permutacja kolejności boków w treści
  [a, b, c] = shuffle([a, b, c]);

  return {
    type: "ClassifyTriangleFromSidesTask",
    data: { a, b, c }
  };
}

// ───────────────────────── renderTask ──────────────────────
export function renderTask({ data }){
  const { a,b,c } = data;
  return (
    <div>
      <div style={{ marginBottom: 8 }}>
        W trójkącie <InlineMath math={"ABC"} /> dane są odcinki:
        <InlineMath math={`\\;a=${a},\\; b=${b},\\; c=${c}.`} /> 
      </div>
      <div>1) Sprawdź, czy z tych odcinków można zbudować trójkąt.</div>
      <div>2) Jeśli tak, sklasyfikuj go:</div>
      <ul>
        <li>po bokach: równoboczny / równoramienny / różnoboczny,</li>
        <li>po kątach: ostrokątny / prostokątny / rozwartokątny.</li>
      </ul>
    </div>
  );
}

// ───────────────────────── renderInput ─────────────────────
export function renderInput({ data }, userAnswer, onChange, disabled){
  const val = {
    exists: "",      // "TAK" | "NIE"
    bySides: "",     // gdy TAK: "rownoboczny" | "rownoramienny" | "roznoboczny"
    byAngles: "",    // gdy TAK: "ostrokatny" | "prostokatny" | "rozwartokatny"
    ...(userAnswer ?? {})
  };

  const row = { display: "flex", alignItems: "center", gap: 10 };

  return (
    <div style={{ display:"grid", gap: 12, marginTop: 6 }}>
      <div style={row}>
        <span>Czy powstaje trójkąt?</span>
        <SelectInput
          value={val.exists}
          onChange={(e)=> onChange({ ...val, exists: e.target.value })}
          disabled={disabled}
          options={[
            // ⬇️ bez dodatkowego "-- Wybierz --"
            { value:"TAK", label:"Tak" },
            { value:"NIE", label:"Nie" }
          ]}
        />
      </div>

      {val.exists === "TAK" && (
        <>
          <div style={row}>
            <span>Klasyfikacja po bokach:</span>
            <SelectInput
              value={val.bySides}
              onChange={(e)=> onChange({ ...val, bySides: e.target.value })}
              disabled={disabled}
              options={[
                // ⬇️ bez placeholdera
                { value:"rownoboczny", label:"równoboczny" },
                { value:"rownoramienny", label:"równoramienny" },
                { value:"roznoboczny",  label:"różnoboczny" }
              ]}
            />
          </div>

          <div style={row}>
            <span>Klasyfikacja po kątach:</span>
            <SelectInput
              value={val.byAngles}
              onChange={(e)=> onChange({ ...val, byAngles: e.target.value })}
              disabled={disabled}
              options={[
                // ⬇️ bez placeholdera
                { value:"ostrokatny",  label:"ostrokątny" },
                { value:"prostokatny", label:"prostokątny" },
                { value:"rozwartokatny", label:"rozwartokątny" }
              ]}
            />
          </div>
        </>
      )}
    </div>
  );
}


// ───────────────────────── checkAnswer ─────────────────────
export function checkAnswer({ data }, userAnswer){
  if (!userAnswer) return false;

  const { a,b,c } = data;
  const exists = isTriangle(a,b,c);
  const existsAns = String(userAnswer.exists || "");

  // Uczeń MUSI wybrać "Tak/Nie"
  if (exists && existsAns !== "TAK") return false;
  if (!exists && existsAns !== "NIE") return false;

  // Jeśli nie ma trójkąta – reszta nie jest wymagana
  if (!exists) return true;

  // Gdy trójkąt istnieje – potrzebne są oba wybory
  const bySides = userAnswer.bySides || "";
  const byAngles = userAnswer.byAngles || "";
  if (!bySides || !byAngles) return false;

  const expSides  = classifyBySides(a,b,c);
  const expAngles = classifyByAngles(a,b,c);

  return bySides === expSides && byAngles === expAngles;
}

// ───────────────────────── renderAnswer ────────────────────
export function renderAnswer({ data }) {
  const { a, b, c } = data;

  if (!isTriangle(a, b, c)) {
    // TaskRunner doda swój nagłówek "Odpowiedź:", więc tu tylko treść
    return <div>trójkąt <em>nie</em> powstaje.</div>;
  }

  const mapSides = {
    rownoboczny: "równoboczny",
    rownoramienny: "równoramienny",
    roznoboczny: "różnoboczny",
  };
  const mapAngles = {
    ostrokatny: "ostrokątny",
    prostokatny: "prostokątny",
    rozwartokatny: "rozwartokątny",
  };

  const side = mapSides[classifyBySides(a, b, c)];
  const angle = mapAngles[classifyByAngles(a, b, c)];

  // Czytelnie w osobnych wierszach
  return (
    <div style={{ display: "grid", gap: 4 }}>
      <div>trójkąt powstaje</div>
      <div>po bokach: <em>{side}</em></div>
      <div>po kątach: <em>{angle}</em></div>
    </div>
  );
}


// ───────────────────────── renderExplanation ───────────────
export function renderExplanation({ data }) {
  const { a, b, c } = data;
  const [x, y, z] = [a, b, c].sort((p, q) => p - q); // x ≤ y ≤ z

  const exists = x + y > z;

  const x2 = x * x;
  const y2 = y * y;
  const z2 = z * z;
  const sum = x2 + y2;

  const cmp = sum === z2 ? "=" : (sum > z2 ? ">" : "<");

  // opisy po bokach / po kątach (gdy istnieje)
  const bySides =
    a === b && b === c
      ? "Wszystkie boki równe → trójkąt równoboczny."
      : (a === b || a === c || b === c)
      ? "Dwa boki równe → trójkąt równoramienny."
      : "Wszystkie boki różne → trójkąt różnoboczny.";

  let byAngles;
  if (sum === z2) byAngles = "Suma kwadratów krótszych równa kwadratowi najdłuższego → prostokątny.";
  else if (sum > z2) byAngles = "Suma kwadratów krótszych większa → ostrokątny.";
  else byAngles = "Suma kwadratów krótszych mniejsza → rozwartokątny.";

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      {/* Krok 1 */}
      <div><strong>Krok 1.</strong> Nierówność trójkąta (wystarczy sprawdzić najdłuższy bok):</div>
      <div style={{ marginTop: 4 }}>
        Porządkujemy boki rosnąco: <InlineMath math={`x\\le y\\le z\\;=\\;${x}\\le ${y}\\le ${z}`} />.
        Wystarczy sprawdzić jedną nierówność z najdłuższym bokiem:
      </div>
      <BlockMath math={`${x} + ${y} > ${z}\\;\\Rightarrow\\; ${exists ? "\\text{tak}" : "\\text{nie}"}`} />

      {!exists ? (
        <div>Skoro <InlineMath math={`${x}+${y}\\le ${z}`} />, z podanych odcinków nie powstaje trójkąt.</div>
      ) : (
        <>
          {/* Krok 2 */}
          <div style={{ marginTop: 10 }}><strong>Krok 2.</strong> Klasyfikacja po bokach:</div>
          <div>{bySides}</div>

          {/* Krok 3 */}
          <div style={{ marginTop: 10 }}>
            <strong>Krok 3.</strong> Klasyfikacja po kątach (odwrócone tw. Pitagorasa — porównujemy z najdłuższym bokiem):
          </div>
          <BlockMath math={`${x}^{2}=${x2}\\,,\\; ${y}^{2}=${y2}\\,,\\; ${z}^{2}=${z2}`} />
          <BlockMath math={`${x}^{2}+${y}^{2}=${sum}\\,,\\; ${z}^{2}=${z2}`} />
          <BlockMath math={`${sum}\\;${cmp}\\;${z2}`} />
          <div>{byAngles}</div>
        </>
      )}
    </div>
  );
}


// ───────────────────────── renderUserAnswer ────────────────
export function renderUserAnswer(arg1, arg2) {
  // Obsługa obu wywołań: (task, userAnswer) ORAZ (userAnswer)
  let ua;
  if (arg2 !== undefined) {
    ua = arg2;
  } else if (arg1 && typeof arg1 === "object") {
    if ("exists" in arg1 || "bySides" in arg1 || "byAngles" in arg1) {
      ua = arg1;
    } else if ("userAnswer" in arg1) {
      ua = arg1.userAnswer;
    }
  }

  if (!ua) return <i>(brak odpowiedzi)</i>;

  const exists = String(ua.exists || "").trim().toUpperCase();
  const bySides = ua.bySides || "";
  const byAngles = ua.byAngles || "";

  const mapSides = {
    rownoboczny: "równoboczny",
    rownoramienny: "równoramienny",
    roznoboczny: "różnoboczny",
  };
  const mapAngles = {
    ostrokatny: "ostrokątny",
    prostokatny: "prostokątny",
    rozwartokatny: "rozwartokątny",
  };

  // Czytelny układ w liniach
  const wrap = (children) => (
    <div style={{ display: "grid", gap: 4 }}>{children}</div>
  );

  if (exists === "NIE") {
    return wrap(<div>trójkąt <em>nie</em> powstaje</div>);
  }

  if (exists !== "TAK") {
    return <i>(najpierw wybierz: „Czy powstaje trójkąt?”)</i>;
  }

  const s = bySides ? mapSides[bySides] : "—";
  const k = byAngles ? mapAngles[byAngles] : "—";

  return wrap(
    <>
      <div>trójkąt powstaje</div>
      <div>po bokach: <em>{s}</em></div>
      <div>po kątach: <em>{k}</em></div>
    </>
  );
}

